import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';
import { getPublicApiUrl } from '../api';
import {
  SubathonElements,
  SubathonOverlayConfig,
  SubathonOverlayName,
  SubathonOverlayPayload,
  SubathonSoundConfig,
  SubathonState,
  SubathonStyle,
} from './types';
import { playSubathonSound } from './sounds';

function getSocketBase() {
  return getPublicApiUrl().replace(/\/api\/?$/, '') || window.location.origin;
}

/** Aviso visual de un aporte: aparece un rato y se va solo. */
export interface CreditFlash {
  id: string;
  label: string;
  secondsAdded: number;
  at: number;
}

const DEMO_STATE: SubathonState = {
  serverNow: new Date().toISOString(),
  enabled: true,
  timer: {
    status: 'running',
    running: true,
    waiting: false,
    remainingMs: 3 * 60 * 60 * 1000 + 24 * 60 * 1000 + 51 * 1000,
    endsAt: null,
    startedAt: new Date().toISOString(),
    finishedAt: null,
    endedReason: '',
    initialMs: 2 * 60 * 60 * 1000,
    addedMs: 84 * 60 * 1000,
    manualMs: 6 * 60 * 1000,
    capMs: 8 * 60 * 60 * 1000,
    capReached: false,
    runId: 'demo',
  },
  totals: {
    events: 37,
    seconds: 84 * 60,
    bySource: { twitch: 58 * 60, kofi: 12 * 60, streamelements: 8 * 60, manual: 6 * 60 },
    byType: {
      sub: { seconds: 25 * 60, count: 5, quantity: 5 },
      subgift: { seconds: 20 * 60, count: 2, quantity: 4 },
      bits: { seconds: 13 * 60, count: 6, quantity: 1300 },
      cafecito: { seconds: 12 * 60, count: 8, quantity: 24 },
      tip: { seconds: 8 * 60, count: 3, quantity: 8 },
      follow: { seconds: 6 * 60, count: 12, quantity: 12 },
    },
    top: [
      { displayName: 'Roberto', seconds: 20 * 60, events: 4 },
      { displayName: 'Ana', seconds: 15 * 60, events: 3 },
      { displayName: 'Juan', seconds: 10 * 60, events: 2 },
      { displayName: 'Sofi', seconds: 8 * 60, events: 5 },
      { displayName: 'Cami', seconds: 5 * 60, events: 1 },
    ],
  },
  recent: [
    {
      id: 'd1',
      source: 'twitch',
      type: 'sub',
      displayName: 'Juan',
      quantity: 1,
      tier: '1000',
      currency: '',
      secondsAdded: 300,
      secondsCapped: 0,
      note: '',
      undone: false,
      label: 'sub de Juan',
      createdAt: new Date().toISOString(),
    },
    {
      id: 'd2',
      source: 'manual',
      type: 'cafecito',
      displayName: 'Roberto',
      quantity: 5,
      tier: '',
      currency: '',
      secondsAdded: 150,
      secondsCapped: 0,
      note: '',
      undone: false,
      label: '5 cafecitos de Roberto',
      createdAt: new Date().toISOString(),
    },
    {
      id: 'd3',
      source: 'twitch',
      type: 'bits',
      displayName: 'Ana',
      quantity: 500,
      tier: '',
      currency: '',
      secondsAdded: 300,
      secondsCapped: 0,
      note: '',
      undone: false,
      label: '500 bits de Ana',
      createdAt: new Date().toISOString(),
    },
  ],
};

const SILENT_SOUND: SubathonSoundConfig & { allowed: boolean } = {
  allowed: false,
  enabled: false,
  volume: 55,
  end: 'none',
  warning: 'none',
  warningMinutes: 5,
};

export function useSubathonOverlay(
  overlayKey: string | undefined,
  overlay: SubathonOverlayName,
  demo: boolean,
) {
  const [config, setConfig] = useState<SubathonOverlayConfig | null>(null);
  const [state, setState] = useState<SubathonState | null>(null);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const [flashes, setFlashes] = useState<CreditFlash[]>([]);

  const [previewStyle, setPreviewStyle] = useState<Partial<SubathonStyle> | null>(null);
  const [previewElements, setPreviewElements] = useState<SubathonElements | null>(null);

  const offsetRef = useRef(0);
  const soundRef = useRef({ ...SILENT_SOUND });
  const lastStatusRef = useRef<string | null>(null);
  const warnedRef = useRef<string | null>(null);

  const applyPayload = useCallback((payload: SubathonOverlayPayload) => {
    setConfig(payload.config);
    setState(payload.state);
    offsetRef.current = Date.parse(payload.state.serverNow) - Date.now();

    const sound = payload.config.style.sound || ({} as SubathonSoundConfig);
    soundRef.current = {
      allowed: !!payload.soundEnabled,
      enabled: !!sound.enabled,
      volume: sound.volume ?? 55,
      end: sound.end || 'none',
      warning: sound.warning || 'none',
      warningMinutes: sound.warningMinutes ?? 5,
    };
  }, []);

  const fetchAll = useCallback(async () => {
    if (!overlayKey) return;
    const apiUrl = getPublicApiUrl();
    const { data } = await axios.get<SubathonOverlayPayload>(
      `${apiUrl}/subathon/public/${overlayKey}/${overlay}`,
    );
    applyPayload(data);
    setError('');
  }, [overlayKey, overlay, applyPayload]);

  useEffect(() => {
    if (!overlayKey) return undefined;

    let socket: Socket | null = null;
    let cancelled = false;

    const setup = async () => {
      try {
        await fetchAll();
      } catch {
        if (!cancelled) setError('Overlay no encontrado o clave inválida.');
        return;
      }
      if (cancelled) return;

      socket = io(getSocketBase(), { path: '/api/socket.io' });

      socket.on('connect', () => {
        socket?.emit('join-subathon', { key: overlayKey });
        fetchAll().catch(() => undefined);
      });

      socket.on('subathon:state', (next: SubathonState) => {
        setState(next);
        offsetRef.current = Date.parse(next.serverNow) - Date.now();
      });

      socket.on('subathon:overlay-config', (data: { overlay: string }) => {
        if (data?.overlay === overlay) fetchAll().catch(() => undefined);
      });

      socket.on('subathon:credit', (data: { id?: string; label?: string; secondsAdded?: number }) => {
        setFlashes((prev) => [
          ...prev.slice(-5),
          {
            id: data?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            label: data?.label || '',
            secondsAdded: data?.secondsAdded || 0,
            at: Date.now(),
          },
        ]);
      });
    };

    setup();
    const poll = window.setInterval(() => {
      fetchAll().catch(() => undefined);
    }, 60000);

    return () => {
      cancelled = true;
      window.clearInterval(poll);
      if (socket) {
        socket.emit('leave-subathon', { key: overlayKey });
        socket.disconnect();
      }
    };
  }, [overlayKey, overlay, fetchAll]);

  // Reloj local: el countdown se deriva de endsAt, sin tráfico por segundo.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  // Los avisos viejos se limpian solos (la duración la decide el overlay).
  useEffect(() => {
    if (!flashes.length) return undefined;
    const id = window.setTimeout(() => {
      setFlashes((prev) => prev.filter((f) => Date.now() - f.at < 30000));
    }, 2000);
    return () => window.clearTimeout(id);
  }, [flashes]);

  // Vista previa en vivo desde el editor del admin (iframe).
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== 'subathon:preview') return;
      if (data.overlay && data.overlay !== overlay) return;
      setPreviewStyle(data.style || null);
      setPreviewElements(data.elements || null);
    };
    window.addEventListener('message', onMessage);
    window.parent?.postMessage({ type: 'subathon:preview-ready', overlay }, '*');
    return () => window.removeEventListener('message', onMessage);
  }, [overlay]);

  const effectiveState = useMemo<SubathonState | null>(() => {
    if (!demo) return state;
    if (!state || state.timer.status === 'idle') return DEMO_STATE;
    return state;
  }, [demo, state]);

  const timer = effectiveState?.timer || null;

  const remainingMs = useMemo(() => {
    if (!timer) return 0;
    if (timer.status === 'running' && timer.endsAt) {
      return Math.max(0, Date.parse(timer.endsAt) - (now + offsetRef.current));
    }
    return timer.remainingMs;
  }, [timer, now]);

  // Sonido del final: suena una sola vez al pasar a finalizado.
  useEffect(() => {
    if (!timer) return;
    const signature = `${timer.runId}:${timer.status}`;
    if (lastStatusRef.current === null) {
      lastStatusRef.current = signature;
      return;
    }
    if (lastStatusRef.current === signature) return;

    const wasRunning = lastStatusRef.current.endsWith(':running');
    lastStatusRef.current = signature;

    const sound = soundRef.current;
    if (!sound.allowed || !sound.enabled) return;
    if (timer.status === 'finished' && wasRunning) {
      playSubathonSound(sound.end, sound.volume);
    }
  }, [timer]);

  // Aviso previo: suena una vez por extensible cuando entra en los últimos minutos.
  useEffect(() => {
    if (!timer || timer.status !== 'running') return;
    const sound = soundRef.current;
    if (!sound.allowed || !sound.enabled || sound.warning === 'none') return;

    const threshold = Math.max(1, sound.warningMinutes) * 60 * 1000;
    if (remainingMs > threshold || remainingMs <= 0) return;

    const key = `${timer.runId}:${sound.warningMinutes}`;
    if (warnedRef.current === key) return;
    warnedRef.current = key;
    playSubathonSound(sound.warning, sound.volume);
  }, [timer, remainingMs]);

  const style = useMemo<SubathonStyle | null>(() => {
    if (!config) return null;
    return { ...config.style, ...(previewStyle || {}) };
  }, [config, previewStyle]);

  const elements = useMemo<SubathonElements>(() => {
    if (!config) return previewElements || {};
    return { ...config.elements, ...(previewElements || {}) };
  }, [config, previewElements]);

  // En la vista previa mostramos avisos de ejemplo. Se mantienen siempre frescos
  // para que no se desvanezcan justo cuando se los está configurando.
  const effectiveFlashes = useMemo<CreditFlash[]>(() => {
    if (!demo || flashes.length > 0) return flashes;
    return (effectiveState?.recent || []).slice(0, 4).map((event, index) => ({
      id: `demo-${event.id}`,
      label: event.label,
      secondsAdded: event.secondsAdded,
      at: now - index * 400,
    }));
  }, [demo, flashes, effectiveState, now]);

  return {
    config,
    style,
    elements,
    state: effectiveState,
    remainingMs,
    flashes: effectiveFlashes,
    error,
    demo,
  };
}
